import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Users, Building2, Link2, ShieldCheck, Archive, RotateCcw, Trash2, Crown, Copy, BarChart3 } from "lucide-react";

const ROLE_DESC = {
  OWNER: "Full control, team, transfer and deletion",
  ADMIN: "Operational control and team management",
  EDITOR: "Create and edit, but cannot publish or manage the team",
  VIEWER: "Read-only access",
};

function RoleBadge({ role }) {
  const c = { OWNER: "text-donkey-orange border-donkey-orange/50", ADMIN: "text-donkey-amber border-donkey-amber/40",
    EDITOR: "text-donkey-green border-donkey-green/40", VIEWER: "text-donkey-grey border-white/20" }[role] || "";
  return <span className={`text-[10px] uppercase tracking-wider border rounded px-1.5 py-0.5 ${c}`} data-testid={`role-badge`}>{role}</span>;
}

export default function TeamManagement() {
  const [data, setData] = useState(null);
  const [sel, setSel] = useState(null);
  const [members, setMembers] = useState(null);
  const [invites, setInvites] = useState([]);
  const [audit, setAudit] = useState([]);
  const [inviteRole, setInviteRole] = useState("EDITOR");
  const [inviteEmail, setInviteEmail] = useState("");
  const [lastToken, setLastToken] = useState(null);
  const [renameVal, setRenameVal] = useState("");
  const [portfolio, setPortfolio] = useState(null);

  const loadBusinesses = useCallback(async () => {
    const { data } = await api.get("/me/businesses");
    setData(data);
    const current = data.selected_business_id && data.businesses.find(b => b.id === data.selected_business_id && !b.archived);
    const pick = current || data.businesses.find(b => !b.archived) || data.businesses[0];
    if (pick) setSel(pick.id);
  }, []);

  const loadBusiness = useCallback(async (bid) => {
    if (!bid) return;
    try {
      const m = await api.get(`/businesses/${bid}/members`);
      setMembers(m.data);
      if (m.data.can_manage_team) {
        const [iv, au] = await Promise.all([api.get(`/businesses/${bid}/invitations`), api.get(`/businesses/${bid}/audit`)]);
        setInvites(iv.data.invitations); setAudit(au.data.audit);
      } else { setInvites([]); setAudit([]); }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, []);

  useEffect(() => { loadBusinesses(); }, [loadBusinesses]);
  useEffect(() => { if (sel) loadBusiness(sel); }, [sel, loadBusiness]);

  const selBiz = data?.businesses.find(b => b.id === sel);
  const alw = data?.allowance;

  const switchTo = async (bid) => {
    setSel(bid); setLastToken(null);
    try { await api.post("/me/selected-business", { business_id: bid }); } catch { /* ignore */ }
  };
  const act = async (fn, ok) => { try { await fn(); toast.success(ok); await loadBusinesses(); await loadBusiness(sel); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  const createInvite = async () => {
    try {
      const { data } = await api.post(`/businesses/${sel}/invitations`, { role: inviteRole, email_restriction: inviteEmail || null });
      const link = `${window.location.origin}/join?token=${data.token}`;
      setLastToken(link);
      await loadBusiness(sel);
      toast.success("Invitation link created — copy and share it privately.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const copyLink = () => { navigator.clipboard?.writeText(lastToken); toast.success("Link copied"); };

  const changeRole = async (uid, role) => act(() => api.put(`/businesses/${sel}/members/${uid}`, { role }), "Role updated");
  const removeMember = async (uid) => act(() => api.delete(`/businesses/${sel}/members/${uid}`), "Member removed");
  const transfer = async (uid) => { if (!window.confirm("Transfer ownership? You will become an Admin.")) return; act(() => api.post(`/businesses/${sel}/transfer`, { to_user_id: uid, confirm: true }), "Ownership transferred"); };
  const revokeInvite = async (iid) => act(() => api.post(`/businesses/${sel}/invitations/${iid}/revoke`), "Invitation revoked");
  const rename = async () => { if (!renameVal.trim()) return; act(() => api.post(`/businesses/${sel}/rename`, { name: renameVal }), "Renamed"); setRenameVal(""); };
  const archive = async () => act(() => api.post(`/businesses/${sel}/archive`), "Business archived");
  const restore = async (bid) => act(() => api.post(`/businesses/${bid}/restore`), "Business restored");
  const del = async () => { const c = window.prompt('Type DELETE to permanently delete this business:'); if (!c) return; act(() => api.delete(`/businesses/${sel}`, { data: { confirm: c } }), "Business deleted"); };

  const loadPortfolio = async () => { try { const { data } = await api.get("/portfolio/report"); setPortfolio(data); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  if (!data) return <div className="min-h-[50vh]" />;
  const canScale = alw && (alw.plan === "scale" || alw.plan === "enterprise");

  return (
    <div className="mx-auto max-w-5xl px-5 sm:px-8 py-12 text-donkey-warm" data-testid="team-page">
      <div className="flex items-center gap-3 mb-2"><Users className="w-7 h-7 text-donkey-orange" /><h1 className="font-heading font-extrabold text-3xl sm:text-4xl">Businesses & Team</h1></div>
      <p className="text-sm text-donkey-grey mb-8">Manage your businesses, invite teammates and control who can do what — enforced on the server.</p>

      {/* Allowance + switcher */}
      <div className="card-donkey rounded-2xl p-5 mb-6" data-testid="business-switcher">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-2 text-sm"><Building2 className="w-4 h-4 text-donkey-orange" />
            <span className="text-donkey-grey">{alw.plan_name} plan · </span>
            <span data-testid="allowance-summary">{alw.unlimited ? "Unlimited active businesses" : `${alw.active_owned} of ${alw.limit} active businesses used${alw.remaining != null ? ` · ${alw.remaining} remaining` : ""}`}</span>
          </div>
          {canScale && <Button size="sm" variant="outline" onClick={loadPortfolio} className="border-white/15" data-testid="load-portfolio-btn"><BarChart3 className="w-4 h-4 mr-1.5" /> Cross-business report</Button>}
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {data.businesses.length === 0 && (
            <div className="col-span-full text-center py-8" data-testid="team-empty-state">
              <p className="text-donkey-grey text-sm mb-3">No businesses yet.</p>
              <Button onClick={() => (window.location.href = "/dashboard")} className="bg-donkey-orange text-donkey-black" data-testid="empty-create-btn">Start with Build</Button>
            </div>
          )}
          {data.businesses.map(b => (
            <button key={b.id} onClick={() => !b.archived && switchTo(b.id)} disabled={b.archived}
              className={`text-left rounded-xl border p-3 transition-colors ${b.id === sel ? "border-donkey-orange bg-donkey-orange/10" : "border-white/12 hover:border-white/25"} ${b.archived ? "opacity-50" : ""}`}
              data-testid={`business-item-${b.id}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium truncate">{b.name}</span><RoleBadge role={b.role} />
              </div>
              <div className="text-xs text-donkey-grey mt-1">{b.archived ? "Archived" : b.published ? "Published" : "Draft"}</div>
              {b.archived && <button onClick={(e) => { e.stopPropagation(); restore(b.id); }} className="text-xs text-donkey-orange underline mt-1" data-testid={`restore-${b.id}`}>Restore</button>}
            </button>
          ))}
        </div>
      </div>

      {portfolio && (
        <div className="card-donkey rounded-2xl p-5 mb-6" data-testid="portfolio-report">
          <h2 className="font-heading font-semibold text-lg mb-3 flex items-center gap-2"><BarChart3 className="w-5 h-5 text-donkey-orange" /> Cross-business report</h2>
          <p className="text-xs text-donkey-grey mb-3">{portfolio.note}</p>
          <div className="overflow-x-auto rounded-lg border border-white/10" role="region" aria-label="Cross-business totals" tabIndex={0}>
            <table className="w-full text-sm min-w-[640px]"><thead><tr className="bg-donkey-deep text-donkey-grey">
              <th className="text-left p-2">Business</th><th className="p-2">Contacts</th><th className="p-2">Customers</th><th className="p-2">Conversion</th><th className="p-2">Verified rev.</th></tr></thead>
              <tbody>{portfolio.businesses.map(s => (<tr key={s.business_id} className="border-t border-white/8">
                <td className="p-2">{s.name}</td><td className="p-2 text-center">{s.contacts}</td><td className="p-2 text-center">{s.customers}</td>
                <td className="p-2 text-center">{s.conversion_pct != null ? `${s.conversion_pct}%` : "Not available"}</td>
                <td className="p-2 text-center">{s.verified_revenue.value != null ? `£${s.verified_revenue.value}` : "Not available"}</td></tr>))}
                <tr className="border-t border-white/20 font-medium"><td className="p-2">Total ({portfolio.totals.business_count})</td>
                  <td className="p-2 text-center">{portfolio.totals.contacts}</td><td className="p-2 text-center">{portfolio.totals.customers}</td><td className="p-2 text-center">—</td>
                  <td className="p-2 text-center">{portfolio.totals.verified_revenue.value != null ? `£${portfolio.totals.verified_revenue.value}` : "Not available"}</td></tr>
              </tbody></table>
          </div>
          <p className="text-xs text-donkey-grey mt-2">{portfolio.totals.method}</p>
        </div>
      )}

      {selBiz && members && (
        <>
          {/* Business admin actions */}
          <div className="card-donkey rounded-2xl p-5 mb-6">
            <h2 className="font-heading font-semibold text-lg mb-1">{selBiz.name}</h2>
            <p className="text-xs text-donkey-grey mb-4">Your role: <RoleBadge role={members.your_role} /> — {ROLE_DESC[members.your_role]}</p>
            <div className="flex flex-wrap gap-2 items-center">
              {["OWNER", "ADMIN", "EDITOR"].includes(members.your_role) && (
                <div className="flex gap-2 items-center">
                  <Input value={renameVal} onChange={e => setRenameVal(e.target.value)} placeholder="Rename business" aria-label="New business name" className="bg-donkey-bg border-white/15 h-9 w-48" data-testid="rename-input" />
                  <Button size="sm" onClick={rename} className="bg-donkey-orange text-donkey-black h-9" data-testid="rename-btn">Rename</Button>
                </div>
              )}
              {members.can_manage_team && !selBiz.archived && <Button size="sm" variant="outline" onClick={archive} className="border-white/15 h-9" data-testid="archive-btn"><Archive className="w-4 h-4 mr-1.5" /> Archive</Button>}
              {members.can_delete && <Button size="sm" variant="outline" onClick={del} className="border-red-400/40 text-red-400 h-9" data-testid="delete-btn"><Trash2 className="w-4 h-4 mr-1.5" /> Delete permanently</Button>}
            </div>
          </div>

          {/* Members */}
          <div className="card-donkey rounded-2xl p-5 mb-6" data-testid="members-list">
            <h3 className="font-heading font-semibold mb-3 flex items-center gap-2"><Users className="w-4 h-4 text-donkey-orange" /> Team members</h3>
            <div className="space-y-2">
              {members.members.map(m => (
                <div key={m.user_id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-white/10 p-3" data-testid={`member-${m.user_id}`}>
                  <div className="min-w-0">
                    <p className="text-sm truncate">{m.email} {m.is_you && <span className="text-donkey-grey">(you)</span>} {m.role === "OWNER" && <Crown className="inline w-3.5 h-3.5 text-donkey-orange" />}</p>
                    <p className="text-xs text-donkey-grey">Joined {String(m.joined_at).slice(0, 10)}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {members.can_manage_team && m.role !== "OWNER" && (
                      <select value={m.role} onChange={e => changeRole(m.user_id, e.target.value)} aria-label={`Role for ${m.email}`} className="bg-donkey-bg border border-white/15 rounded px-2 py-1 text-xs" data-testid={`role-select-${m.user_id}`}>
                        {["ADMIN", "EDITOR", "VIEWER"].map(r => <option key={r} value={r}>{r}</option>)}
                      </select>
                    )}
                    {members.your_role === "OWNER" ? <RoleBadge role={m.role} /> : null}
                    {members.can_transfer && m.role !== "OWNER" && <Button size="sm" variant="ghost" onClick={() => transfer(m.user_id)} className="h-8 text-xs border border-white/10" data-testid={`transfer-${m.user_id}`}>Make owner</Button>}
                    {members.can_manage_team && !m.is_you && m.role !== "OWNER" && <Button size="sm" variant="ghost" onClick={() => removeMember(m.user_id)} aria-label={`Remove ${m.email}`} className="h-8 text-xs text-red-400" data-testid={`remove-${m.user_id}`}>Remove</Button>}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Invitations */}
          {members.can_manage_team && (
            <div className="card-donkey rounded-2xl p-5 mb-6" data-testid="invitations-panel">
              <h3 className="font-heading font-semibold mb-3 flex items-center gap-2"><Link2 className="w-4 h-4 text-donkey-orange" /> Invite by link (no email is sent)</h3>
              <div className="flex flex-wrap gap-2 items-end">
                <div><label className="text-xs text-donkey-grey">Role</label><br />
                  <select value={inviteRole} onChange={e => setInviteRole(e.target.value)} aria-label="Invitation role" className="bg-donkey-bg border border-white/15 rounded px-2 py-1.5 text-sm mt-1" data-testid="invite-role">
                    {["ADMIN", "EDITOR", "VIEWER"].map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
                <Input value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} placeholder="Restrict to email (optional)" aria-label="Restrict invitation to email" className="bg-donkey-bg border-white/15 h-9 w-56" data-testid="invite-email" />
                <Button size="sm" onClick={createInvite} className="bg-donkey-orange text-donkey-black h-9" data-testid="create-invite-btn">Create link</Button>
              </div>
              {lastToken && (
                <div className="mt-3 flex items-center gap-2 rounded-lg bg-donkey-orange/10 border border-donkey-orange/25 p-3">
                  <code className="text-xs break-all flex-1" data-testid="invite-link">{lastToken}</code>
                  <Button size="sm" variant="ghost" onClick={copyLink} aria-label="Copy invitation link" className="border border-white/10"><Copy className="w-4 h-4" /></Button>
                </div>
              )}
              <p className="text-xs text-donkey-grey mt-2">Links are single-use, expire in 72 hours, and are shown only once.</p>
              <p className="text-xs text-donkey-grey mt-1" data-testid="plan-inheritance-note">Invited teammates use the business owner's plan and do not need to purchase a separate subscription. Access still follows the owner's plan limits and each teammate's role.</p>
              <div className="mt-4 space-y-1.5">
                {invites.filter(i => i.status === "pending").map(i => (
                  <div key={i.id} className="flex items-center justify-between text-xs border border-white/10 rounded p-2" data-testid={`invite-${i.id}`}>
                    <span>{i.role}{i.email_restriction ? ` · ${i.email_restriction}` : ""} · expires {String(i.expires_at).slice(0, 10)}</span>
                    <button onClick={() => revokeInvite(i.id)} className="text-red-400 underline" data-testid={`revoke-${i.id}`}>Revoke</button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Audit */}
          {members.can_manage_team && audit.length > 0 && (
            <div className="card-donkey rounded-2xl p-5" data-testid="audit-panel">
              <h3 className="font-heading font-semibold mb-3 flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-donkey-orange" /> Activity log</h3>
              <div className="space-y-1 max-h-64 overflow-y-auto" role="region" aria-label="Team activity log" tabIndex={0}>
                {audit.map((a, i) => (
                  <p key={i} className="text-xs text-donkey-grey"><span className="text-donkey-warm/80">{a.action.replace(/_/g, " ")}</span> · {a.actor_email || "system"} · {String(a.at).slice(0, 16).replace("T", " ")}</p>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
